import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import UploadRounded from '@mui/icons-material/UploadRounded';
import CreateNewFolderRounded from '@mui/icons-material/CreateNewFolderRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import Swal from 'sweetalert2';
import { Checkbox } from '@mui/material';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';
import {
  listDocumentFolders, createDocumentFolder, deleteDocumentFolder,
  listDocuments, uploadDocument, deleteDocument, getDocumentStats,
  DocumentFolderItem, DocumentItem,
} from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import TabFilterBar from '../components/ui/TabFilterBar';
import EmptyState from '../components/ui/EmptyState';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'pdf', label: 'PDF' },
  { value: 'office', label: 'Office' },
  { value: 'other', label: 'Others' },
];

const FILE_TYPE_COLOR: Record<DocumentItem['fileType'], string> = {
  image: '#0277bd',
  pdf: '#CF1E52',
  office: '#0ca30c',
  other: '#6226A7',
};

const fileIconFor = (type: DocumentItem['fileType']) => {
  switch (type) {
    case 'image': return <ImageRounded />;
    case 'pdf': return <PictureAsPdfRounded />;
    case 'office': return <DescriptionRounded />;
    default: return <InsertDriveFileRounded />;
  }
};

const fmtSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const [tab, setTab] = useState('all');
  const [folders, setFolders] = useState<DocumentFolderItem[]>([]);
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const bulk = useBulkSelect();
  const [stats, setStats] = useState({ total: 0, images: 0, pdf: 0, office: 0, others: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, d, s] = await Promise.all([
        listDocumentFolders(folderId),
        listDocuments({ folder: folderId || 'root', type: tab === 'all' ? undefined : tab }),
        getDocumentStats(),
      ]);
      setFolders(f);
      setDocs(d);
      setStats(s);
    } catch {
      setFolders([]);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [folderId, tab]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      await uploadDocument(file, { folder: folderId || undefined });
    }
    load();
  };

  const handleNewFolder = async () => {
    const result = await Swal.fire({
      title: 'New folder',
      input: 'text',
      inputPlaceholder: 'Folder name',
      showCancelButton: true,
      confirmButtonText: 'Create',
    });
    const name = String(result.value || '').trim();
    if (!result.isConfirmed || !name) return;
    await createDocumentFolder(name, folderId || null);
    load();
  };

  const handleDeleteFolder = async (f: DocumentFolderItem) => {
    const result = await Swal.fire({ title: `Delete "${f.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    try {
      await deleteDocumentFolder(f._id);
      load();
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Folder is not empty', 'error');
    }
  };

  const handleDeleteDoc = async (d: DocumentItem) => {
    const result = await Swal.fire({ title: `Delete "${d.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    await deleteDocument(d._id);
    load();
  };

  // Folders and files share one selection; ids are prefixed so deleteOne knows which API to call.
  const deleteSelected = (key: string) =>
    key.startsWith('f:') ? deleteDocumentFolder(key.slice(2)) : deleteDocument(key.slice(2));
  const visibleKeys = [...folders.map((f) => `f:${f._id}`), ...docs.map((d) => `d:${d._id}`)];

  const isEmpty = !loading && folders.length === 0 && docs.length === 0;

  return (
    <Box>
      <PageHeader
        title="Documents"
        subtitle={`${stats.total} files`}
        actions={
          <>
            <Button startIcon={<CreateNewFolderRounded />} variant="outlined" onClick={handleNewFolder}>New Folder</Button>
            <Button startIcon={<UploadRounded />} variant="contained" onClick={() => fileInputRef.current?.click()}>Upload</Button>
            <input ref={fileInputRef} type="file" hidden multiple onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }} />
          </>
        }
      />

      {folderId && (
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/documents')} sx={{ mb: 2 }}>
          Back to Documents
        </Button>
      )}

      <TabFilterBar value={tab} onChange={setTab} options={TABS} />

      <BulkSelectBar bulk={bulk} visibleIds={visibleKeys} noun="item" deleteOne={deleteSelected} onDone={load} />

      {isEmpty ? (
        <EmptyState icon={FolderRounded} title="No documents yet" description="Upload a file or create a folder to get started." actionLabel="Upload" onAction={() => fileInputRef.current?.click()} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
          {folders.map((f) => (
            <Paper
              key={f._id}
              sx={{ p: 2, cursor: 'pointer', textAlign: 'center', position: 'relative', '&:hover .doc-delete': { opacity: 1 } }}
              onClick={() => navigate(`/documents/${f._id}`)}
            >
              <Checkbox
                size="small"
                checked={bulk.isSelected(`f:${f._id}`)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => bulk.toggle(`f:${f._id}`)}
                sx={{ position: 'absolute', top: 2, left: 2, p: 0.5 }}
              />
              <IconButton
                size="small"
                className="doc-delete"
                sx={{ position: 'absolute', top: 4, right: 4, opacity: 0, transition: 'opacity .15s' }}
                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(f); }}
              >
                <DeleteRounded fontSize="small" />
              </IconButton>
              <FolderRounded sx={{ fontSize: 40, color: '#F6A938' }} />
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, mt: 0.5 }}>{f.name}</Typography>
            </Paper>
          ))}

          {docs.map((d) => (
            <Paper
              key={d._id}
              sx={{ p: 2, textAlign: 'center', position: 'relative', '&:hover .doc-delete': { opacity: 1 } }}
            >
              <Checkbox
                size="small"
                checked={bulk.isSelected(`d:${d._id}`)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => bulk.toggle(`d:${d._id}`)}
                sx={{ position: 'absolute', top: 2, left: 2, p: 0.5 }}
              />
              <IconButton
                size="small"
                className="doc-delete"
                sx={{ position: 'absolute', top: 4, right: 4, opacity: 0, transition: 'opacity .15s' }}
                onClick={() => handleDeleteDoc(d)}
              >
                <DeleteRounded fontSize="small" />
              </IconButton>
              <Box component="a" href={d.url} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <Box sx={{
                  width: 48, height: 48, mx: 'auto', borderRadius: 1, display: 'grid', placeItems: 'center',
                  bgcolor: `${FILE_TYPE_COLOR[d.fileType]}1F`, color: FILE_TYPE_COLOR[d.fileType],
                }}>
                  {fileIconFor(d.fileType)}
                </Box>
                <Typography variant="body2" noWrap title={d.name} sx={{ fontWeight: 600, mt: 0.5 }}>{d.name}</Typography>
                <Stack direction="row" justifyContent="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">{fmtSize(d.size)}</Typography>
                  <Typography variant="caption" color="text.secondary">·</Typography>
                  <Typography variant="caption" color="text.secondary">{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : ''}</Typography>
                </Stack>
              </Box>
            </Paper>
          ))}

          <Tooltip title="Upload file">
            <Paper
              sx={{ p: 2, textAlign: 'center', cursor: 'pointer', border: '1px dashed', borderColor: 'divider', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadRounded sx={{ fontSize: 32, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Upload File</Typography>
            </Paper>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default DocumentsPage;
